import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ActionIconButton from '@components/buttons/ActionIconButton';
import RequiredFieldIndicator from '@components/fields/RequiredFieldIndicator';
import styles from '@components/fieldsNative/SearchInput.module.css';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import FormError from '@components/typography/FormError';
import useOpener from '@hooks/useOpener';

type Props = {
  error: string | null | undefined;
  errorValues?: { [key: string]: string | ReactNode };
  initialValue: string | undefined | null;
  label?: string | ReactNode;
  name: string;
  noSpacing?: boolean;
  placeholder?: string;
  required?: boolean;
  setValue: (value: string) => void;
  skipIcon?: boolean;
  value: string | null | undefined;
};

const AutosaveInput = ({
  error,
  errorValues,
  initialValue,
  label,
  name,
  noSpacing,
  required,
  setValue,
  value
}: Props) => {
  const opener = useOpener();

  const [editingValue, setEditingValue] = useState(value);

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [textAreaRows, setTextAreaRows] = useState(1);

  useEffect(() => {
    if (opener.isOpen) {
      inputRef.current?.focus();
    }
  }, [opener.isOpen]);

  const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const height = event.target.scrollHeight;
    const rowHeight = 30;

    const trows = Math.ceil(height / rowHeight) - 1;

    if (trows !== textAreaRows) {
      setTextAreaRows(trows);
    }

    setEditingValue(event.target.value);
  };

  useEffect(() => {
    if (!opener.isOpen && editingValue !== value) {
      setValue(editingValue ?? '');
    }
  }, [editingValue, opener.isOpen, setValue, value]);

  return (
    <div className={`${styles.inputWrapper} ${noSpacing ? styles.noMargin : ''}`}>
      <div className={styles.relativeWrapper}>
        <div className={styles.labelRow}>
          <div>
            {label && (
              <div>
                <BodyCopyBold>
                  {label}

                  {required && <RequiredFieldIndicator />}
                </BodyCopyBold>
              </div>
            )}
          </div>
        </div>

        <div className={opener.isOpen ? styles.relativeWrapper : 'ion-hide'}>
          <textarea
            aria-invalid={error?.length ? 'true' : 'false'}
            aria-label={name}
            aria-required={required}
            className={`${styles.autosaveInput} ${editingValue ? '' : styles.uneditedInput}`}
            defaultValue={initialValue ?? ''}
            name={name}
            onBlur={opener.close}
            onChange={handleInputChange}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                opener.close();
              }
            }}
            ref={inputRef}
            rows={textAreaRows}
          />
          <div className={styles.addIcon}>
            <ActionIconButton
              ariaLabelKey="accessibility.ariaLabels.add"
              color="neutral200"
              icon="add"
              onClick={opener.close}
            />
          </div>
        </div>

        <div
          className={opener.isOpen ? 'ion-hide' : value ? styles.fakeInput : styles.fakeInput + ' ' + styles.hover}
          onClick={opener.open}
        >
          {value ? (
            <div>
              <BodyCopyBold color="primary700">{value}</BodyCopyBold>
              <div className={styles.editIcon}>
                <ActionIconButton ariaLabelKey="accessibility.ariaLabels.edit" icon="edit" onClick={opener.open} />
              </div>
            </div>
          ) : (
            <BodyCopyBold color="neutral500">
              <FormattedMessage id="dictionary.addSomethingElse" />
            </BodyCopyBold>
          )}
        </div>

        {error && (
          <div>
            <FormError>
              <FormattedMessage id={error} values={errorValues} />
            </FormError>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutosaveInput;
